// Player vitals UI: the bottom-left HP bar, the dev free-build tag, the bleeding-out (downed) vignette,
// and the death screen (fade to ~90% black with DEAD, then a RESPAWN button). All driven each frame
// from the player state machine via update(); the body-drop on death and the respawn point are game
// concerns, so they come in as callbacks (onDied / onRespawn).
import { SK, btnCss, redHover, injectSkinCss } from './skin';

export interface VitalsState {
  health: number;
  maxHealth: number;
  pendingHeal: number; // buffered HP that fills in slowly (the ghost "gain marker" on the bar)
  hunger: number;
  thirst: number;
  maxHunger: number;
  maxThirst: number;
  bleeding: boolean;
  poisoned: boolean;
  state: 'alive' | 'downed' | 'dead';
  downedTimer: number;
  reviveChance: number;
  devFree: boolean;
  godMode: boolean;
}

export class Vitals {
  private readonly healthFill: HTMLDivElement;
  private readonly healthPending: HTMLDivElement; // ghost fill: shows current + pending heal
  private readonly hungerFill: HTMLDivElement;
  private readonly thirstFill: HTMLDivElement;
  private readonly healthNum: HTMLDivElement;
  private readonly hungerNum: HTMLDivElement;
  private readonly thirstNum: HTMLDivElement;
  private readonly statusTag: HTMLDivElement; // bleeding / poisoned indicator
  private readonly devTag: HTMLDivElement;
  private readonly godTag: HTMLDivElement;
  private readonly downedTag: HTMLDivElement;
  private readonly downedText: HTMLDivElement;
  private readonly deathOverlay: HTMLDivElement;
  private readonly respawnList: HTMLDivElement; // death screen: Random Beach + each sleeping bag / bed

  private deathElapsed = 0;
  private corpseDropped = false; // one body per death

  onRespawn: ((bagId?: string) => void) | null = null; // respawn at a bag/bed (id) or a random beach (undefined)
  onDied: (() => void) | null = null;    // fired once when the player enters 'dead'

  constructor(private readonly deathFadeSeconds: number) {
    injectSkinCss();
    // Vitals stack (bottom-left), style guide "vitals" pattern: FIXED order health, food, water --
    // [icon][track][tabular number] rows; water is tinted BLUE so the two cream bars stop being twins.
    const healthWrap = document.createElement('div');
    healthWrap.style.cssText = `position:fixed;left:20px;bottom:20px;z-index:50;width:236px;pointer-events:none;display:flex;flex-direction:column;gap:12px;font-family:${SK.font};text-shadow:0 1px 2px rgba(0,0,0,0.7)`;
    const ICON = { heart: '❤', meat: '🍖', drop: '💧' };
    // Build one bar row; returns { fill, num, pending? }. Health gets a pending ghost fill behind its fill.
    const rowIcon = (glyph: string): HTMLDivElement => { const d = document.createElement('div'); d.textContent = glyph; d.style.cssText = 'width:18px;text-align:center;font-size:14px;flex:none;filter:saturate(0.85)'; return d; };
    const makeRow = (glyph: string, fillColor: string, pending: boolean): { fill: HTMLDivElement; num: HTMLDivElement; ghost?: HTMLDivElement } => {
      const row = document.createElement('div'); row.style.cssText = 'display:flex;align-items:center;gap:10px;height:14px';
      const track = document.createElement('div'); track.style.cssText = 'position:relative;flex:1;height:12px;border-radius:4px;background:rgba(255,255,255,0.06);overflow:hidden';
      let ghost: HTMLDivElement | undefined;
      if (pending) { ghost = document.createElement('div'); ghost.style.cssText = `position:absolute;inset:0;width:0;background:${SK.goodSoft};transition:width 0.2s ease-out`; track.appendChild(ghost); }
      const fill = document.createElement('div'); fill.style.cssText = `position:absolute;inset:0;width:100%;border-radius:inherit;transition:width 0.15s ease-out,background 0.2s linear;background:${fillColor}`;
      track.appendChild(fill);
      const num = document.createElement('div'); num.style.cssText = `font:700 ${SK.fsSmall} ${SK.font};font-variant-numeric:tabular-nums;color:${SK.dim};width:30px;text-align:right;flex:none`; num.textContent = '0';
      row.append(rowIcon(glyph), track, num); healthWrap.appendChild(row);
      return { fill, num, ghost };
    };
    const h = makeRow(ICON.heart, SK.health, true);
    this.healthFill = h.fill; this.healthPending = h.ghost!; this.healthNum = h.num;
    const hu = makeRow(ICON.meat, SK.food, false); this.hungerFill = hu.fill; this.hungerNum = hu.num;
    const th = makeRow(ICON.drop, SK.water, false); this.thirstFill = th.fill; this.thirstNum = th.num;
    // Bleeding / poisoned status line under the bars.
    this.statusTag = document.createElement('div');
    this.statusTag.style.cssText = `font:700 ${SK.fsMicro} ${SK.font};letter-spacing:0.08em;display:none`;
    healthWrap.appendChild(this.statusTag);
    document.body.appendChild(healthWrap);

    // Dev free-build indicator (toggled with `) -- semantic BANNER at screen top (system states live
    // outside windows, style guide "banners" pattern).
    const bannerCss = (color: string, soft: string): string => `position:fixed;left:50%;transform:translateX(-50%);z-index:60;display:none;gap:8px;align-items:center;padding:5px 14px;border-radius:${SK.rSm};font:700 ${SK.fsMicro} ${SK.font};letter-spacing:0.1em;text-transform:uppercase;border:1px solid ${color};color:${color};background:${soft};pointer-events:none;text-shadow:none`;
    this.devTag = document.createElement('div');
    this.devTag.textContent = 'Dev build · free craft (` to toggle)';
    this.devTag.style.cssText = bannerCss(SK.warn, SK.warnSoft) + ';top:10px';
    document.body.appendChild(this.devTag);

    // God-mode (no-death) indicator, dev only. Sits just under the dev-build banner.
    this.godTag = document.createElement('div');
    this.godTag.textContent = 'God mode · no death';
    this.godTag.style.cssText = bannerCss(SK.good, SK.goodSoft) + ';top:42px';
    document.body.appendChild(this.godTag);

    // Bleeding-out (downed) overlay: a red vignette + the bleed-out countdown.
    this.downedTag = document.createElement('div');
    this.downedTag.style.cssText = 'position:fixed;inset:0;z-index:55;pointer-events:none;display:none;background:radial-gradient(circle at 50% 50%,transparent 36%,rgba(135,0,0,0.62))';
    this.downedText = document.createElement('div');
    this.downedText.style.cssText = `position:absolute;left:0;right:0;top:34%;text-align:center;color:#ffd9d9;font:800 24px ${SK.font};letter-spacing:0.1em;text-transform:uppercase;text-shadow:0 2px 6px #000`;
    this.downedTag.appendChild(this.downedText);
    document.body.appendChild(this.downedTag);

    // Death overlay, style guide "fullscreen-scrim" pattern: fade toward black, uppercase display
    // type, then the respawn options (primary = the one accent action per view).
    this.deathOverlay = document.createElement('div');
    this.deathOverlay.style.cssText = 'position:fixed;inset:0;z-index:70;display:none;flex-direction:column;align-items:center;justify-content:center;gap:28px;background:#000;opacity:0;pointer-events:none';
    const deadText = document.createElement('div');
    deadText.textContent = 'YOU DIED';
    deadText.style.cssText = `color:${SK.textHi};font:800 52px ${SK.font};letter-spacing:${SK.lsDisplay};text-transform:uppercase`;
    // Respawn options: a column of buttons (Random Beach + each placed bag/bed). Rebuilt only when the set changes
    // (setRespawnPoints), so a click is never lost to a per-frame rebuild. Hidden until the death fade completes.
    this.respawnList = document.createElement('div');
    this.respawnList.style.cssText = 'display:none;flex-direction:column;align-items:center;gap:10px;min-width:260px';
    this.deathOverlay.append(deadText, this.respawnList);
    document.body.appendChild(this.deathOverlay);
    this.setRespawnPoints([]); // default: just Random Beach
  }

  // (Re)build the death-screen respawn buttons. `opts` are the placed bags/beds; "Random Beach" is always offered.
  // A cooling-down point is shown disabled. Called by main when the player dies (and when the set/cooldowns change).
  setRespawnPoints(opts: Array<{ id?: string; label: string; ready: boolean }>): void {
    this.respawnList.replaceChildren();
    const all = [{ id: undefined as string | undefined, label: 'Random Beach', ready: true }, ...opts];
    let first = true;
    for (const o of all) {
      const b = document.createElement('button');
      b.textContent = o.label;
      const on = o.ready;
      // First ready option is THE primary (one per view); the rest are secondary outlines.
      b.style.cssText = btnCss(first && on ? 'primary' : 'secondary') + `;width:100%;height:40px;opacity:${on ? 1 : 0.4};cursor:${on ? 'pointer' : 'not-allowed'}`;
      if (on) { b.onclick = () => this.onRespawn?.(o.id); if (!first) redHover(b); first = false; }
      this.respawnList.appendChild(b);
    }
  }

  update(s: VitalsState, dt: number): void {
    const hp = Math.max(0, s.health);
    this.healthFill.style.width = ((hp / s.maxHealth) * 100).toFixed(1) + '%';
    // Ghost marker = current + pending heal (clamped to full), so you see the gain it is filling toward.
    this.healthPending.style.width = (Math.min(1, (hp + Math.max(0, s.pendingHeal)) / s.maxHealth) * 100).toFixed(1) + '%';
    this.healthNum.textContent = String(Math.round(hp));
    // Low HP runs the fill hot red (matches the reference lowHealth cue).
    this.healthFill.style.background = hp / s.maxHealth <= 0.3 ? SK.bad : SK.health;
    this.hungerFill.style.width = (Math.max(0, s.hunger) / s.maxHunger * 100).toFixed(1) + '%';
    this.thirstFill.style.width = (Math.max(0, s.thirst) / s.maxThirst * 100).toFixed(1) + '%';
    this.hungerNum.textContent = String(Math.round(Math.max(0, s.hunger) / s.maxHunger * 100));
    this.thirstNum.textContent = String(Math.round(Math.max(0, s.thirst) / s.maxThirst * 100));
    // Status: bleeding takes priority over poisoned (both drain HP).
    if (s.bleeding) { this.statusTag.textContent = 'BLEEDING — use a bandage'; this.statusTag.style.color = SK.bad; this.statusTag.style.display = 'block'; }
    else if (s.poisoned) { this.statusTag.textContent = 'POISONED'; this.statusTag.style.color = SK.good; this.statusTag.style.display = 'block'; }
    else this.statusTag.style.display = 'none';
    this.devTag.style.display = s.devFree ? 'inline-flex' : 'none';
    this.godTag.style.display = s.godMode ? 'inline-flex' : 'none';

    if (s.state === 'downed') {
      this.downedTag.style.display = 'block';
      this.downedText.textContent = `BLEEDING OUT — ${Math.ceil(s.downedTimer)}s   ·   recovery ${Math.round(s.reviveChance * 100)}%`;
    } else {
      this.downedTag.style.display = 'none';
    }

    if (s.state === 'dead') {
      if (!this.corpseDropped) {
        this.corpseDropped = true;
        this.onDied?.(); // leave the body + carried resources where you fell
        this.deathElapsed = 0;
        this.deathOverlay.style.display = 'flex';
        this.deathOverlay.style.pointerEvents = 'auto';
      }
      this.deathElapsed += dt;
      this.deathOverlay.style.opacity = String(Math.min(0.9, (this.deathElapsed / this.deathFadeSeconds) * 0.9));
      this.respawnList.style.display = this.deathElapsed >= this.deathFadeSeconds ? 'flex' : 'none';
    } else {
      this.corpseDropped = false; // reset for the next death (on revive/respawn)
      if (this.deathOverlay.style.display !== 'none') {
        this.deathOverlay.style.display = 'none';
        this.deathOverlay.style.pointerEvents = 'none';
        this.respawnList.style.display = 'none';
      }
    }
  }
}
